import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { adminApi } from '../../utils/api';
import './AdminApplicationsPage.css';

const STATUSES = ['All','New','Under Review','Shortlisted','Discovery Meeting Scheduled','Selected','Not Selected','Converted to Paid Lead','Closed'];

const BADGE_MAP = {
  'New':'new','Under Review':'review','Shortlisted':'shortlisted','Selected':'selected',
  'Not Selected':'rejected','Discovery Meeting Scheduled':'meeting',
  'Converted to Paid Lead':'converted','Closed':'closed',
};

export default function AdminApplicationsPage() {
  const [apps, setApps] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('All');
  const [search, setSearch] = useState('');
  const [consultation, setConsultation] = useState(false);
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 15 };
      if (filter !== 'All') params.status = filter;
      if (search) params.search = search;
      if (consultation) params.consultation = true;
      const { data } = await adminApi.getApplications(params);
      setApps(data.applications);
      setTotal(data.total);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [filter, search, consultation, page]);

  useEffect(() => { load(); }, [load]);

  const quickStatus = async (id, status, e) => {
    e.preventDefault();
    await adminApi.updateStatus(id, status);
    load();
  };

  const handleExport = async () => {
    try {
      const params = { format: 'xlsx' };
      if (filter !== 'All') params.status = filter;
      const res = await adminApi.exportApplications(params);
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'adeona_applications.xlsx'; a.click();
      URL.revokeObjectURL(url);
    } catch { alert('Export failed.'); }
  };

  return (
    <div className="apps-page">
      <div className="apps-page__header">
        <div>
          <h1>Applications</h1>
          <p className="text-muted text-small">{total} total applications</p>
        </div>
        <button className="btn btn--outline" onClick={handleExport}>Export Excel</button>
      </div>

      {/* Search */}
      <div className="apps-search">
        <input type="text" placeholder="Search by company, contact or email..."
          value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
        <label className="consult-toggle">
          <input type="checkbox" checked={consultation} onChange={e => { setConsultation(e.target.checked); setPage(1); }} />
          Consultation only
        </label>
      </div>

      {/* Status filters */}
      <div className="status-filters">
        {STATUSES.map(s => (
          <button key={s} className={`filter-chip${filter === s ? ' active' : ''}`}
            onClick={() => { setFilter(s); setPage(1); }}>
            {s}
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div className="page-loading"><div className="spinner" /></div>
      ) : apps.length === 0 ? (
        <div style={{ padding: '40px 24px', textAlign: 'center', color: 'var(--text3)' }}>No applications found.</div>
      ) : (
        <div className="app-list">
          {apps.map(a => (
            <Link key={a.id} to={`/admin/applications/${a.id}`} className="app-row">
              <div className="app-row__main">
                <div className="app-row__company">{a.company_name}</div>
                <div className="app-row__meta">{a.industry} · {a.district} · {a.employee_count} employees</div>
                <div className="app-row__contact">{a.contact_person} · {a.email}</div>
              </div>
              <div className="app-row__right">
                <span className={`badge badge--${BADGE_MAP[a.status] || 'new'}`}>{a.status}</span>
                {a.total_score > 0 && (
                  <div className="app-row__score">{a.total_score}<span>/100</span></div>
                )}
                {a.consultation_interest && (
                  <div className="consult-dot" title="Wants consultation">💬</div>
                )}
                <div className="app-row__actions" onClick={e => e.preventDefault()}>
                  <button className="action-btn" onClick={e => quickStatus(a.id, 'Shortlisted', e)}>Shortlist</button>
                  <button className="action-btn action-btn--danger" onClick={e => quickStatus(a.id, 'Not Selected', e)}>Reject</button>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Pagination */}
      {total > 15 && (
        <div className="pagination">
          <button className="btn btn--outline" disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
          <span className="text-small text-muted">Page {page} of {Math.ceil(total / 15)}</span>
          <button className="btn btn--outline" disabled={page >= Math.ceil(total / 15)} onClick={() => setPage(p => p + 1)}>Next →</button>
        </div>
      )}
    </div>
  );
}
